import { ALM_WEB_COMPONENT_ELEMENTS } from '../../webcomponents/alm-web-component-elements';
import { EventName, SignEventRequest } from '../@types/eventSignRequestBody';
import { SIGNABLE_EVENT_NAMES } from '../@types/signableEvents';
import { WidgetEvent } from '../@types/widgetEvents';
import { verificatorResource } from '../resource/verificator.resource';
import { createUnsignedJWT, parseJWT } from './jwtUtils';

export type EmitWidgetEventReturnType = {
  eventName: string;
  eventValue: any;
  isSuccess: boolean;
};

export type EmitWidgetEventOptions = {
  noSign?: boolean;
  sessionId?: string;
  corrId?: string;
};

const EVENT_NAMES: ReadonlySet<string> = new Set<string>(SIGNABLE_EVENT_NAMES);

function isSignableEvent(eventName: string): eventName is EventName {
  return EVENT_NAMES.has(eventName);
}

export type WidgetEventCallback = (data: EmitWidgetEventReturnType, token: string) => void;

export type ProcessPhaseCallback = (phase: string, timestamp: string) => void;

export function onWidgetEvent(callback: WidgetEventCallback, element: HTMLElement | Document = document): () => void {
  const handleEvent = (event: Event) => {
    const customEvent = event as CustomEvent<{ token: string }>;
    const { token } = customEvent.detail;
    if (token) {
      // Decode JWT payload without verification (base64 decode middle part)
      try {
        const payload = parseJWT<{ eventName: string; eventValue: unknown; isSuccess: boolean }>(token);
        if (!payload) throw new Error('Failed to decode JWT');

        const data: EmitWidgetEventReturnType = {
          eventName: payload.eventName,
          eventValue: payload.eventValue,
          isSuccess: payload.isSuccess,
        };
        callback(data, token);
      } catch (error) {
        console.error('ALM: Error decoding token:', error);
      }
    }
  };

  element.addEventListener('alm-widget-event', handleEvent);

  // Return cleanup function
  return () => {
    element.removeEventListener('alm-widget-event', handleEvent);
  };
}

export function onProcessPhase(callback: ProcessPhaseCallback, element: HTMLElement | Document = document): () => void {
  const handleEvent = (event: Event) => {
    const customEvent = event as CustomEvent<{ phase: string; timestamp: string }>;
    const { phase, timestamp } = customEvent.detail;
    callback(phase, timestamp);
  };

  element.addEventListener('alm-process-phase', handleEvent);

  // Return cleanup function
  return () => {
    element.removeEventListener('alm-process-phase', handleEvent);
  };
}

function normalizeSignEventData(data: EmitWidgetEventReturnType, sessionId: string): SignEventRequest {
  const base: SignEventRequest = {
    sessionId,
    eventName: data.eventName as EventName,
    isSuccess: data.isSuccess,
  };

  const value = data.eventValue;

  switch (data.eventName) {
    case WidgetEvent.EmailPrechecked:
      if (!data.isSuccess && value?.reason) {
        base.reason = value.reason;
      }
      break;

    case WidgetEvent.TechnicalError:
      if (value?.reason) {
        base.reason = value.reason;
      } else if (value?.details) {
        base.reason = typeof value.details === 'string' ? value.details : JSON.stringify(value.details);
      }
      break;

    case WidgetEvent.ProcessFinished:
      if (value?.banned) {
        base.reason = 'banned';
      }
      break;

    // These events don't carry a reason
    case WidgetEvent.EmailPrecheckLogin:
    case WidgetEvent.EmailVerificationStatus:
    case WidgetEvent.SMSVerificationBypass:
    case WidgetEvent.SMSVerification:
    case WidgetEvent.SMSVerificationStatus:
      break;
  }

  return base;
}

async function EmitWidgetEvent(data: EmitWidgetEventReturnType, options: EmitWidgetEventOptions = {}): Promise<void> {
  const { noSign = false, sessionId, corrId } = options;
  const noSignEvent = noSign || !isSignableEvent(data.eventName);

  try {
    const rootElement = findWebComponentRoot();
    let token: string;

    if (noSignEvent) {
      token = createUnsignedJWT(data);
    } else {
      // Sign via backend endpoint for tamper-proof tokens
      const signRequest = normalizeSignEventData(data, sessionId || '');
      try {
        const response = await verificatorResource.signEvent(signRequest as any, corrId);
        token = response.data.token;
      } catch (signError) {
        console.error('ALM: signEvent failed:', signError);
        if (data.eventName === WidgetEvent.TechnicalError) {
          token = createUnsignedJWT(data);
        } else {
          token = createUnsignedJWT({
            eventName: WidgetEvent.TechnicalError,
            eventValue: { reason: 'Failed to sign event', details: signError },
            isSuccess: false,
          });
        }
      }
    }

    const event = new CustomEvent('alm-widget-event', {
      detail: { token },
      bubbles: true,
      composed: true, // Allow event to cross shadow DOM boundary
    });

    rootElement.dispatchEvent(event);
  } catch (error) {
    console.error('ALM: Error emitting data:', error);
  }
}

//Ez az emittáló függvény használható bárhol a kódbázisban
export const emitWidgetEvent = async (data: any, options?: EmitWidgetEventOptions) => {
  await EmitWidgetEvent(data, options);
};

export function emitProcessPhase(phase: string): void {
  try {
    const rootElement = findWebComponentRoot();

    const event = new CustomEvent('alm-process-phase', {
      detail: {
        phase: phase,
        timestamp: new Date().toISOString(),
      },
      bubbles: true,
      composed: true,
    });

    rootElement.dispatchEvent(event);
  } catch (error) {
    console.error('ALM: Error emitting process phase:', error);
  }
}

function findWebComponentRoot(): HTMLElement | Document {
  const tags = Object.values(ALM_WEB_COMPONENT_ELEMENTS);

  // Strategy 1: Look for any known ALM web component by tag name
  for (const tag of tags) {
    const el = document.querySelector(tag);
    if (el) {
      return el as HTMLElement;
    }
  }

  // Strategy 2: Look for any known ALM web component by ID
  for (const tag of tags) {
    const el = document.getElementById(tag);
    if (el) {
      return el;
    }
  }

  // Strategy 3: Try to find any element with the component tag
  const anyALMComponent = document.querySelector('[id*="alm"]');
  if (anyALMComponent) {
    return anyALMComponent as HTMLElement;
  }

  // Fallback to document for dev mode
  return document;
}
